import { API_URLS } from "@/utils/constant/api.constant";
import { C_API } from "@/utils/helper/api";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";

interface NotificationResponse {
  notifications: any[];
  totalCount: number;
  count: number;
  page: number;
  per_page: number;
}

const fetchNotifications = async (page: number = 1, per_page: number = 10): Promise<NotificationResponse> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append("page", page.toString());
    queryParams.append("per_page", per_page.toString());

    const { data } = await C_API.get(`${API_URLS.NOTIFICATION.LIST}?${queryParams.toString()}`);

    return data.data;
  } catch (error) {
    console.error("Error fetching notifications:", error);
    throw error;
  }
};

interface MarkAsReadPayload {
  ids: number[];
  mode: "single" | "all";
}

const markNotificationsAsRead = async (payload: MarkAsReadPayload): Promise<any> => {
  try {
    const { data } = await C_API.post(API_URLS.NOTIFICATION.MARK_AS_READ, payload);
    return data;
  } catch (error) {
    console.error("Error marking notifications as read:", error);
    throw error;
  }
};

interface ClearNotificationsPayload {
  ids: number[];
  mode: "single" | "all";
}

const clearNotifications = async (payload: ClearNotificationsPayload): Promise<any> => {
  try {
    const { data } = await C_API.post(API_URLS.NOTIFICATION.MARK_AS_CLEAR, payload);
    return data;
  } catch (error) {
    console.error("Error clearing notifications:", error);
    throw error;
  }
};

export const useNotifications = (isModalOpen: boolean) => {
  return useInfiniteQuery({
    queryKey: ["notifications"],
    queryFn: ({ pageParam = 1 }) => fetchNotifications(pageParam, 10),
    getNextPageParam: (lastPage: NotificationResponse, allPages) => {
      const loadedCount = allPages.reduce((sum, page) => sum + page.notifications.length, 0);
      const totalAvailable = lastPage.totalCount;
      if (loadedCount >= totalAvailable) {
        return undefined;
      }
      return lastPage.page + 1;
    },
    enabled: isModalOpen,
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes (formerly cacheTime)
    initialPageParam: 1,
  });
};

export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markNotificationsAsRead,
    onSuccess: (_, variables) => {
      // Optimistically update the cache
      queryClient.setQueryData(["notifications"], (oldData: any) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page: NotificationResponse) => ({
            ...page,
            notifications: page.notifications.map((notification: any) => {
              const shouldMarkAsRead = variables.mode === "all" || variables.ids.includes(notification.id);

              return shouldMarkAsRead ? { ...notification, isSeen: true } : notification;
            }),
            count:
              variables.mode === "all"
                ? 0
                : Math.max(0, page.count - variables.ids.filter((id) => page.notifications.some((n: any) => n.id === id && !n.isSeen)).length),
          })),
        };
      });
    },
  });
};

export const useMarkAsClear = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: clearNotifications,
    onSuccess: (_, variables) => {
      // Optimistically update the cache
      queryClient.setQueryData(["notifications"], (oldData: any) => {
        if (!oldData) return oldData;

        if (variables.mode === "all") {
          // Clear all notifications - keep only first page structure
          return {
            ...oldData,
            pages: [
              {
                notifications: [],
                totalCount: 0,
                count: 0,
                page: 1,
                per_page: 10,
              },
            ],
            pageParams: [1], // Reset page params
          };
        } else {
          // Clear specific notifications
          const updatedPages = oldData.pages.map((page: NotificationResponse) => {
            const filteredNotifications = page.notifications.filter((notification: any) => !variables.ids.includes(notification.id));

            const removedNotifications = page.notifications.filter((notification: any) => variables.ids.includes(notification.id));

            const unreadRemovedCount = removedNotifications.filter((n: any) => !n.isSeen).length;

            return {
              ...page,
              notifications: filteredNotifications,
              totalCount: Math.max(0, page.totalCount - removedNotifications.length),
              count: Math.max(0, page.count - unreadRemovedCount),
            };
          });

          // Filter out empty pages except the first one
          const nonEmptyPages = updatedPages.filter((page: NotificationResponse, index: number) => index === 0 || page.notifications.length > 0);

          return {
            ...oldData,
            pages:
              nonEmptyPages.length > 0
                ? nonEmptyPages
                : [
                    {
                      notifications: [],
                      totalCount: 0,
                      count: 0,
                      page: 1,
                      per_page: 10,
                    },
                  ],
            pageParams: nonEmptyPages.length > 0 ? oldData.pageParams.slice(0, nonEmptyPages.length) : [1],
          };
        }
      });
    },
  });
};
